import React, { useState, useEffect } from 'react';

const ThemeChangeModal = ({ isOpen, onClose, themeName, message }) => {
  const [stage, setStage] = useState('init'); // 'init', 'cat', 'typing', 'complete'
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);

  const initCommands = [
    'Initializing theme loader...',
    'Loading color palette...',
    'Applying theme configuration...',
    'Mounting components...',
    'Theme initialization complete ✓'
  ];

  const [commandIndex, setCommandIndex] = useState(0);
  const [commandText, setCommandText] = useState('');

  useEffect(() => {
    if (!isOpen) {
      // Reset everything when modal closes
      setStage('init');
      setDisplayedText('');
      setCurrentIndex(0);
      setCommandIndex(0);
      setCommandText('');
      return;
    }

    // Start initialization sequence
    if (stage === 'init') {
      const timer = setTimeout(() => {
        if (commandIndex < initCommands.length) {
          setCommandText(initCommands[commandIndex]);
          setCommandIndex(prev => prev + 1);
        } else {
          setTimeout(() => {
            setStage('cat');
          }, 1000);
        }
      }, 800);
      return () => clearTimeout(timer);
    }

    // Start cat command and typing
    if (stage === 'cat') {
      setTimeout(() => {
        setStage('typing');
      }, 1000);
    }

    // Typing animation
    if (stage === 'typing' && currentIndex < message.length) {
      const timer = setTimeout(() => {
        setDisplayedText(message.slice(0, currentIndex + 1));
        setCurrentIndex(prev => prev + 1);
      }, 80); // Typing speed
      return () => clearTimeout(timer);
    }

    // Complete
    if (stage === 'typing' && currentIndex >= message.length) {
      setTimeout(() => {
        setStage('complete');
      }, 1000);
    }

  }, [isOpen, stage, commandIndex, currentIndex, message]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 p-4">
      <div className="bg-[#1a0d26] border-2 border-[#4c1d95] rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
        {/* Terminal Header */}
        <div className="bg-[#2d1b69] px-4 py-2 flex items-center gap-2 flex-shrink-0">
          <div className="w-3 h-3 rounded-full bg-[#f472b6]"></div>
          <div className="w-3 h-3 rounded-full bg-[#fbbf24]"></div>
          <div className="w-3 h-3 rounded-full bg-[#a78bfa]"></div>
          <span className="ml-2 text-[#e9d5ff] text-sm font-mono">Theme Loader v2.0</span>
        </div>

        {/* Terminal Content - Scrollable */}
        <div className="p-4 sm:p-6 font-mono text-xs sm:text-sm flex-1 overflow-y-auto">
          {/* Initialization Stage */}
          {stage === 'init' && (
            <div className="space-y-2">
              {initCommands.slice(0, commandIndex).map((cmd, idx) => (
                <div key={idx} className="text-[#c084fc]">
                  <span className="text-[#a855f7]">root@lolo:~$</span> {cmd}
                </div>
              ))}
              {commandIndex < initCommands.length && (
                <div className="text-[#c084fc] animate-pulse">
                  <span className="text-[#a855f7]">root@lolo:~$</span> {commandText}
                </div>
              )}
            </div>
          )}

          {/* Cat Command Stage */}
          {stage === 'cat' && (
            <div className="space-y-2">
              {initCommands.map((cmd, idx) => (
                <div key={idx} className="text-[#c084fc]">
                  <span className="text-[#a855f7]">root@lolo:~$</span> {cmd}
                </div>
              ))}
              <div className="text-[#c084fc] mt-4">
                <span className="text-[#a855f7]">root@lolo:~$</span> cat message.txt
              </div>
            </div>
          )}

          {/* Typing Stage */}
          {(stage === 'typing' || stage === 'complete') && (
            <div className="space-y-2">
              {initCommands.map((cmd, idx) => (
                <div key={idx} className="text-[#c084fc]">
                  <span className="text-[#a855f7]">root@lolo:~$</span> {cmd}
                </div>
              ))}
              <div className="text-[#c084fc] mt-4">
                <span className="text-[#a855f7]">root@lolo:~$</span> cat message.txt
              </div>
              <div className="mt-4 p-3 sm:p-4 bg-[#0f0819] border border-[#4c1d95] rounded">
                <div className="text-[#e9d5ff] text-sm sm:text-lg leading-relaxed break-words">
                  {displayedText}
                  {stage === 'typing' && (
                    <span className="animate-pulse text-[#d946ef]">|</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Complete Stage - Fixed at bottom */}
        {stage === 'complete' && (
          <div className="p-4 sm:p-6 flex justify-center border-t border-[#4c1d95] bg-[#1a0d26] flex-shrink-0">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-[#a855f7] text-white rounded-lg hover:bg-[#d946ef] transition-colors duration-200 font-medium text-sm sm:text-base"
            >
              Continue
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ThemeChangeModal; 